import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  ListGroup,
  Badge,
  Pagination,
  InputGroup,
  Image,
  Table,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const BANK_CONFIG = {
  accountNumber: "101000537033",
  accountName: "NGUYEN HUNG KIEN",
  bankCode: "PVCB",
  bankName: "PVcomBank",
};

const getLevelNameFromId = (id) => {
  switch (id) {
    case 7: return "G";
    case 1: return "F";
    case 2: return "E";
    case 3: return "D";
    case 4: return "C";
    case 5: return "B";
    case 6: return "A";
    default: return `ID ${id}`;
  }
};

const formatOptionLabel = ({ value, label }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span>{label}</span>
    <a
      href={`/players/${value}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="btn btn-sm btn-outline-primary"
    >
      Xem
    </a>
  </div>
);

const TournamentRegistration = () => {
  const { uuid } = useParams();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer1, setSelectedPlayer1] = useState(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const playerOptions = players.map((p) => ({
    value: p.id,
    label: `${p.id} - ${p.name} (Hạng: ${getLevelNameFromId(p.level_present)})`,
  }));

  // Function tạo QR code
  const generateQRCodeUrl = (player1Id, player2Id = null) => {
    if (!tournament) return null;

    const amount = tournament.type === "single" ? 300000 : 500000;
    let description = "";

    if (tournament.type === "single") {
      description = `${player1Id} - ${tournament.name}`;
    } else {
      if (!player2Id) return null;
      description = `${player1Id}, ${player2Id} - ${tournament.name}`;
    }

    const encodedDescription = encodeURIComponent(description);
    return `https://qr.sepay.vn/img?acc=${BANK_CONFIG.accountNumber}&bank=${BANK_CONFIG.bankCode}&amount=${amount}&des=${encodedDescription}`;
  };

  const fetchTournamentData = useCallback(async () => {
    setLoading(true);
    try {
      const [tourneyRes, playersRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/register/${uuid}`),
        axios.get(`${API_URL}/playerspublic?limit=all`),
      ]);
      setTournament(tourneyRes.data);
      setPlayers(playersRes.data);
    } catch (err) {
      setNotification({
        type: "danger",
        msg: "Không thể tải thông tin giải đấu hoặc giải đấu không tồn tại.",
      });
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchTournamentData();
  }, [fetchTournamentData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlayer1 || (tournament.type === "double" && !selectedPlayer2)) {
      setNotification({ type: "warning", msg: "Vui lòng chọn đủ vận động viên." });
      return;
    }
    if (tournament.type === "double" && selectedPlayer1.value === selectedPlayer2.value) {
      setNotification({ type: "warning", msg: "Vui lòng chọn 2 vận động viên khác nhau." });
      return;
    }

    setSubmitting(true);
    setNotification({ type: "", msg: "" });

    try {
      await axios.post(`${API_URL}/tournaments/${uuid}/register`, {
        player_1_id: selectedPlayer1.value,
        player_2_id: tournament.type === "double" ? selectedPlayer2.value : null,
      });
      setNotification({
        type: "success",
        msg: "Đăng ký thành công! Vui lòng chờ xác nhận từ ban tổ chức.",
      });
      setSelectedPlayer1(null);
      setSelectedPlayer2(null);
      fetchTournamentData();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi gửi đăng ký.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!tournament) {
    return (
      <Alert variant="danger">
        {notification.msg || "Không tìm thấy giải đấu."}
      </Alert>
    );
  }

  // Move derived state calculations inside the render block, after the guards.
  const playerMap = new Map(players.map((p) => [p.id, p]));

  const filteredRegistrations =
    (tournament.registrations || []).filter((reg) => {
      const p1Name = playerMap.get(reg.player_1_id)?.name.toLowerCase() || "";
      const p2Name = playerMap.get(reg.player_2_id)?.name.toLowerCase() || "";
      return (
        p1Name.includes(searchTerm.toLowerCase()) ||
        p2Name.includes(searchTerm.toLowerCase())
      );
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRegistrations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const isFull = (tournament.registrations || []).length >= tournament.player_limit;

  // QR code cho form đăng ký mới
  const formQRCodeUrl = selectedPlayer1
    ? generateQRCodeUrl(
        selectedPlayer1.value,
        tournament.type === "double" ? selectedPlayer2?.value : null
      )
    : null;

  // QR code cho người đã đăng ký (chỉ hiện khi tìm được 1 người và chưa duyệt)
  const showRegisteredQR = filteredRegistrations.length === 1 && searchTerm;
  const foundRegistration = showRegisteredQR ? filteredRegistrations[0] : null;
  const showPaymentQR = foundRegistration && !foundRegistration.approved;

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={8}>
          <Card>
            <Card.Header as="h2" className="text-center">
              {tournament.name}
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush" className="mb-4">
                <ListGroup.Item>
                  <strong>Hạng đấu áp dụng:</strong>{" "}
                  {(tournament.level_ids || []).map(getLevelNameFromId).join(", ")}
                </ListGroup.Item>
                {tournament.start_date && (
                  <ListGroup.Item>
                    <strong>Thời gian:</strong>{" "}
                    {new Date(tournament.start_date).toLocaleDateString("vi-VN")}
                  </ListGroup.Item>
                )}
                {tournament.location && (
                  <ListGroup.Item>
                    <strong>Địa điểm:</strong> {tournament.location}
                  </ListGroup.Item>
                )}
                <ListGroup.Item>
                  <strong>Loại hình:</strong>{" "}
                  {tournament.type === "single" ? "Đơn" : "Đôi"}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Số lượng đã đăng ký:</strong>{" "}
                  <Badge bg="success">
                    {tournament.registrations.length} / {tournament.player_limit}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Lệ phí:</strong>{" "}
                  <Badge bg="info">
                    {tournament.type === "single" ? "300,000" : "500,000"} VND
                  </Badge>
                </ListGroup.Item>
              </ListGroup>

              <h4 className="mt-4">Danh sách VĐV đã đăng ký</h4>
              <InputGroup className="mb-3">
                <Form.Control
                  placeholder="Tìm kiếm VĐV..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>

              {filteredRegistrations.length > 0 ? (
                <>
                  <ListGroup>
                    {currentItems.map((reg, index) => {
                      const player1 = playerMap.get(reg.player_1_id);
                      const player2 = playerMap.get(reg.player_2_id);
                      const player1Name = player1
                        ? `${player1.name} (Hạng: ${getLevelNameFromId(player1.level_present)}) (ID: ${player1.id})`
                        : `VĐV ID: ${reg.player_1_id}`;
                      const player2Name = player2
                        ? `${player2.name} (Hạng: ${getLevelNameFromId(player2.level_present)}) (ID: ${player2.id})`
                        : null;
                      return (
                        <ListGroup.Item
                          key={index}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <span>
                            <strong>{indexOfFirstItem + index + 1}. </strong>
                            {player1Name}
                            {player2Name && ` & ${player2Name}`}
                          </span>
                          <Badge bg={reg.approved ? "success" : "secondary"}>
                            {reg.approved ? "Đã duyệt" : "Chưa duyệt"}
                          </Badge>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                  {totalPages > 1 && (
                    <Pagination className="mt-3 justify-content-center">
                      {[...Array(totalPages).keys()].map((number) => (
                        <Pagination.Item
                          key={number + 1}
                          active={number + 1 === currentPage}
                          onClick={() => paginate(number + 1)}
                        >
                          {number + 1}
                        </Pagination.Item>
                      ))}
                    </Pagination>
                  )}
                </>
              ) : (
                <p>
                  {searchTerm
                    ? "Không tìm thấy VĐV nào."
                    : "Chưa có VĐV nào đăng ký."}
                </p>
              )}

              {/* QR CODE CHO NGƯỜI ĐÃ ĐĂNG KÝ - CHỈ HIỆN KHI CHƯA DUYỆT */}
              {showPaymentQR && (() => {
                const player1 = playerMap.get(foundRegistration.player_1_id);
                const player2 = playerMap.get(foundRegistration.player_2_id);

                const qrUrl = generateQRCodeUrl(
                  foundRegistration.player_1_id,
                  foundRegistration.player_2_id
                );

                const amount = tournament.type === "single" ? "300,000" : "500,000";
                const description = tournament.type === "single"
                  ? `${player1?.id} - ${tournament.name}`
                  : `${player1?.id}, ${player2?.id} - ${tournament.name}`;

                return (
                  <>
                    <hr className="my-4" />
                    <h4 className="text-center text-warning mb-3">
                      ⏳ Chưa Thanh Toán - Vui Lòng Hoàn Tất
                    </h4>
                    <Card className="mb-3 border-warning">
                      <Card.Header className="bg-warning text-dark text-center">
                        <strong>THÔNG TIN THANH TOÁN</strong>
                      </Card.Header>
                      <Card.Body className="text-center">
                        <Alert variant="warning" className="mb-3">
                          <div className="text-start">
                            <strong>👤 Người đăng ký:</strong>{" "}
                            {player1?.name}
                            {player2 && ` & ${player2.name}`}
                            <br />
                            <strong>💰 Lệ phí:</strong>{" "}
                            <span className="text-danger fw-bold">{amount} VND</span>
                            <br />
                            <strong>📝 Nội dung chuyển khoản:</strong>{" "}
                            <code>{description}</code>
                            <br />
                            <strong>⚠️ Trạng thái:</strong>{" "}
                            <Badge bg="danger">Chưa duyệt - Chưa thanh toán</Badge>
                          </div>
                        </Alert>

                        <div className="mb-3">
                          <Image
                            src={qrUrl}
                            alt="QR Code Thanh toán"
                            fluid
                            style={{
                              maxWidth: "300px",
                              margin: "0 auto",
                              border: "2px solid #ffc107",
                              borderRadius: "8px",
                              padding: "10px",
                            }}
                          />
                        </div>

                        <Card className="mb-3" style={{ maxWidth: "400px", margin: "0 auto" }}>
                          <Card.Header className="bg-light">
                            <strong>📋 Thông tin tài khoản nhận tiền</strong>
                          </Card.Header>
                          <Card.Body>
                            <Table bordered size="sm" className="mb-0">
                              <tbody>
                                <tr>
                                  <td className="text-start"><strong>Ngân hàng:</strong></td>
                                  <td className="text-end">
                                    <Badge bg="primary">{BANK_CONFIG.bankName}</Badge>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start"><strong>Số tài khoản:</strong></td>
                                  <td className="text-end">
                                    <code className="text-primary fs-6">{BANK_CONFIG.accountNumber}</code>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start"><strong>Chủ tài khoản:</strong></td>
                                  <td className="text-end fw-bold">{BANK_CONFIG.accountName}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>

                        <Alert variant="danger" className="mt-3 mb-0">
                          <small>
                            🚨 <strong>Quan trọng:</strong> Vui lòng thanh toán đúng số tiền và nội dung chuyển khoản để được duyệt tự động.
                          </small>
                        </Alert>
                      </Card.Body>
                    </Card>
                  </>
                );
              })()}

              <hr />

              {notification.msg && (
                <Alert variant={notification.type}>{notification.msg}</Alert>
              )}

              {isFull ? (
                <Alert variant="warning">Giải đấu đã đủ số lượng.</Alert>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <h4 className="mb-3">Form Đăng ký</h4>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {tournament.type === "double" ? "Vận động viên 1" : "Vận động viên"}
                    </Form.Label>
                    <Select
                      options={playerOptions}
                      value={selectedPlayer1}
                      onChange={setSelectedPlayer1}
                      isSearchable
                      placeholder="-- Gõ để tìm VĐV --"
                      noOptionsMessage={() => "Không tìm thấy VĐV"}
                      formatOptionLabel={formatOptionLabel}
                    />
                  </Form.Group>

                  {tournament.type === "double" && (
                    <Form.Group className="mb-3">
                      <Form.Label>Vận động viên 2</Form.Label>
                      <Select
                        options={playerOptions}
                        value={selectedPlayer2}
                        onChange={setSelectedPlayer2}
                        isSearchable
                        placeholder="-- Gõ để tìm VĐV --"
                        noOptionsMessage={() => "Không tìm thấy VĐV"}
                        formatOptionLabel={formatOptionLabel}
                      />
                    </Form.Group>
                  )}

                  {/* QR CODE CHO FORM ĐĂNG KÝ MỚI */}
                  {formQRCodeUrl && (
                    <Card className="mb-3">
                      <Card.Header className="bg-primary text-white text-center">
                        <strong>THÔNG TIN THANH TOÁN</strong>
                      </Card.Header>
                      <Card.Body className="text-center">
                        <Alert variant="info" className="mb-3">
                          <div className="text-start">
                            <strong>💰 Lệ phí:</strong>{" "}
                            <span className="text-danger fw-bold">
                              {tournament.type === "single" ? "300,000" : "500,000"} VND
                            </span>
                            <br />
                            <strong>📝 Nội dung chuyển khoản:</strong>{" "}
                            <code>
                              {tournament.type === "single"
                                ? `${selectedPlayer1.value} - ${tournament.name}`
                                : `${selectedPlayer1.value}, ${selectedPlayer2?.value} - ${tournament.name}`}
                            </code>
                          </div>
                        </Alert>

                        <div className="mb-3">
                          <Image
                            src={formQRCodeUrl}
                            alt="QR Code Thanh toán"
                            fluid
                            style={{
                              maxWidth: "300px",
                              margin: "0 auto",
                              border: "2px solid #dee2e6",
                              borderRadius: "8px",
                              padding: "10px",
                            }}
                          />
                        </div>

                        <Card className="mb-3" style={{ maxWidth: "400px", margin: "0 auto" }}>
                          <Card.Header className="bg-light">
                            <strong>📋 Thông tin tài khoản nhận tiền</strong>
                          </Card.Header>
                          <Card.Body>
                            <Table bordered size="sm" className="mb-0">
                              <tbody>
                                <tr>
                                  <td className="text-start"><strong>Ngân hàng:</strong></td>
                                  <td className="text-end">
                                    <Badge bg="primary">{BANK_CONFIG.bankName}</Badge>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start"><strong>Số tài khoản:</strong></td>
                                  <td className="text-end">
                                    <code className="text-primary fs-6">{BANK_CONFIG.accountNumber}</code>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-start"><strong>Chủ tài khoản:</strong></td>
                                  <td className="text-end fw-bold">{BANK_CONFIG.accountName}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>

                        <Alert variant="warning" className="mt-3 mb-0">
                          <small>
                            ⚠️ <strong>Lưu ý:</strong> Vui lòng kiểm tra kỹ thông tin tài khoản trước khi chuyển tiền.
                          </small>
                        </Alert>
                      </Card.Body>
                    </Card>
                  )}

                  <Button type="submit" variant="success" disabled={submitting} className="w-100">
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Đang xử lý...
                      </>
                    ) : (
                      "✅ Gửi Đăng ký"
                    )}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TournamentRegistration;
